import React from "react";
import { useTranslation } from "react-i18next";
import useScrollAnimation from "@/hooks/useScrollAnimation";
import { PROJECTS, Project } from "@/data/projects";
import { TESTIMONIALS } from "@/data/testimonials";
import "./SocialProofSection.scss";

type ProjectStatus = "live" | "beta" | "openSource";

function getStatus(project: Project): ProjectStatus {
  if (project.tags.some((tag) => tag.toLowerCase().includes("testflight"))) return "beta";
  if (project.tags.some((tag) => tag.toLowerCase().includes("open source"))) return "openSource";
  return "live";
}

const SocialProofSection: React.FC = () => {
  const { t } = useTranslation();
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1 });

  return (
    <section className="social-proof-section" ref={ref}>
      <div id="social-proof-section" className="container">
        <div className={`sp-head reveal${isVisible ? " in" : ""}`}>
          <span className="sec-label">{t("socialProof.label")}</span>
          <h2 className="sec-title">
            {(() => {
              const title = t("socialProof.title");
              const hl = t("socialProof.titleHighlight");
              const [before, after = ""] = title.split(hl);
              return (
                <>
                  {before}
                  <em>{hl}</em>
                  {after}
                </>
              );
            })()}
          </h2>
        </div>

        <div className={`sp-products reveal${isVisible ? " in" : ""}`} style={{ transitionDelay: "0.1s" }}>
          <p className="sp-products-label">{t("socialProof.inProductionLabel")}</p>
          <ul className="sp-product-list" aria-label={t("socialProof.inProductionLabel")}>
            {PROJECTS.map((project, i) => {
              const status = getStatus(project);
              return (
                <li
                  key={project.key}
                  className="sp-product-item"
                  style={{ transitionDelay: `${0.15 + i * 0.05}s` }}
                >
                  <span className="sp-product-name">{project.name}</span>
                  <span className={`sp-status-pill sp-status-pill--${status}`}>
                    {t(`socialProof.status.${status}`)}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>

        {TESTIMONIALS.length > 0 && (
          <div
            className={`sp-testimonials reveal${isVisible ? " in" : ""}`}
            style={{ transitionDelay: "0.3s" }}
          >
            <ul className="sp-testimonials-list">
              {TESTIMONIALS.map((item, i) => (
                <li key={i} className="sp-testimonial">
                  <blockquote className="sp-testimonial-quote">"{item.quote}"</blockquote>
                  <cite className="sp-testimonial-cite">
                    <span className="sp-testimonial-author">{item.author}</span>
                    <span className="sp-testimonial-role">
                      {item.role}, {item.company}
                    </span>
                  </cite>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
};

export default SocialProofSection;
